import type {RouteLocationNormalized} from 'vue-router';
import {storeToRefs} from 'pinia';
import {createGtm, type VueGtmUseOptions} from '@gtm-support/vue-gtm';
import {useCustomerProfileStore} from '~/stores/customerProfile';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {useShoppingListsStore} from '~/stores/shoppingLists';
import {getShoppingListIdForGtm} from '~/utils/tracking';
import {GTM_ID} from '#shared/constants';

function additionalGtmData(to: RouteLocationNormalized, from: RouteLocationNormalized) {
	const {profile, userIsLogged} = storeToRefs(useCustomerProfileStore());
	const userLoggedTrackEvent = {
		user_is_logged: userIsLogged.value,
		...profile.value?.registration?.customerID
		&& {user_id: profile.value?.registration?.customerID},
		...profile.value?.registration?.globusClubs?.find((club) => club.clubID === 4)
		&& {user_scan_and_go_level: profile.value?.registration?.globusClubs?.find((club) => club.clubID === 4)?.levelName},
		...profile.value?.registration?.globusClubs?.find((club) => club.clubID === 7)
		&& {user_globus_bonus_level: profile.value?.registration?.globusClubs?.find((club) => club.clubID === 7)?.levelName},
		...profile.value?.registration?.globusClubs?.find((club) => club.clubID === 8)
		&& {user_mimi_club_level: profile.value?.registration?.globusClubs?.find((club) => club.clubID === 8)?.levelName},
	};

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const hypermarketTrackEvent = {
		...selectedHypermarketInstance.value && {
			selected_hypermarket: selectedHypermarketInstance.value?.content.name,
			selected_hypermarket_id: selectedHypermarketInstance.value?.content.gsoaId,
		},
	};

	const shoppingListsStore = useShoppingListsStore();
	const {selectedShoppingList, selectedShoppingListTotalCount} = storeToRefs(shoppingListsStore);
	const shoppingListUniqItems = selectedShoppingListTotalCount.value ?? 0;
	const shoppingListTrackEvent = {
		shopping_list_uniq_items: shoppingListUniqItems,
		...(shoppingListUniqItems > 0 && selectedShoppingList.value != null
			&& {shopping_list_id: getShoppingListIdForGtm(selectedShoppingList.value)}),
	};

	return {
		category: 'page',
		...from.fullPath !== to.fullPath && {route_from_full_path: from.fullPath},
		route_full_path: to.fullPath,
		title: to.meta.title ?? document.title,
		route_name: to.name,
		page_title: to.meta.title ?? document.title,
		page_path: to.fullPath,
		page_location: window.location.href,
		...to.hash && {route_hash: to.hash},
		...to.params.id && {route_params_id: to.params.id},
		...to.params.slug && {route_params_slug: to.params.slug},
		...to.params.category
		&& {
			route_params_category: typeof to.params.category === 'object'
				? to.params.category.join(',')
				: to.params.category,
		},
		...to.params.vanr && {route_params_vanr: to.params.vanr},
		...to.params.branch && {route_params_branch: to.params.branch},
		...to.redirectedFrom && {redirected_from: to.redirectedFrom.fullPath},
		...userLoggedTrackEvent,
		...hypermarketTrackEvent,
		...shoppingListTrackEvent,
		...(to.params.branch) && {content_hypermarket: to.params.branch},
		page_type: getGtmPageType(to),
		content_group: null,
	};
}

export default defineNuxtPlugin((nuxtApp) => {
	const {isSpeedMode} = useSpeedMode();

	if (isSpeedMode.value) { return; }

	const router = useRouter();
	const gtmOptions: VueGtmUseOptions = {
		id: GTM_ID,
		debug: process.env.NODE_ENV !== 'production',
		loadScript: false,
		vueRouter: router,
		trackOnNextTick: true,
		trackViewEventProperty: 'gtm_route_middleware',
		vueRouterAdditionalEventData: additionalGtmData,
		ignoredViews: (to, from) => {
			return (from.name === 'recepty-category' || from.name === 'blog-category')
				&& (!('sorting' in from.query) && !('authors' in from.query) && !('time' in from.query) && !('variants' in from.query))
				&& (to.name === 'recepty-category' || to.name === 'blog-category')
				&& ('sorting' in to.query || 'authors' in to.query || 'time' in to.query || 'variants' in to.query);
		},
	};

	nuxtApp.vueApp.use(createGtm(gtmOptions));
});
